/**
 * Application providers.
 *
 * TanStack Query is the only place server data lives. Copying it into a client
 * store is the main source of stale-UI bugs, so there is no Redux here and none
 * is planned. docs/07-frontend.md §3
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { ApiError } from "@/shared/api/errors";
import { AuthProvider } from "@/shared/auth/AuthContext";
import { LanguageProvider } from "@/shared/i18n/LanguageProvider";

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Tuned per data class at the call site; this is the conservative
        // default. Money and unpublished marks always override to 0.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Never retry a rejection the server meant: a 403 will not become a
          // 200, and retrying a 429 makes the rate limit worse.
          if (error instanceof ApiError && !error.isRetryable) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Mutations are never retried automatically. Writes here move money and
        // academic records; an idempotency key is the correct mechanism, not a
        // blind retry. docs/06-api-design.md §6
        retry: false,
      },
    },
  });
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Outside AuthProvider on purpose: the login screen needs a language
          too, and a school's staff should be able to switch before they are
          able to sign in. */}
      <LanguageProvider>
        <AuthProvider>{children}</AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
